import { Injectable } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

/** An entity currently rendered on screen (e.g. the unit/charge being viewed). */
export interface OnScreenEntity {
    type: string;
    id: number;
}

/** Extra context recorded with an on-screen entity. */
export interface PageContextDetails {
    id: number;
    organizationId: number;
}

/**
 * Tracks which domain entities are currently on screen so a bug report filed
 * from a detail page carries `entitiesOnScreen` (and lets the server snapshot
 * the right records). Detail route components call `setContext()` from their
 * `ngOnInit` after they read the id from `ActivatedRoute`.
 *
 * The registry is cleared automatically on every navigation start so stale
 * entities from a previous page can't leak into a report filed on the next one.
 */
@Injectable({ providedIn: 'root' })
export class PageContextService {
    private _entities = new Map<string, OnScreenEntity>();

    constructor(private _router: Router) {
        this._router.events
            .pipe(filter((e): e is NavigationStart => e instanceof NavigationStart))
            .subscribe(() => this.clear());
    }

    /**
     * Register an on-screen entity. Keyed by `type:id` so re-registering the
     * same entity (e.g. on a reactive param change) replaces rather than
     * duplicates it.
     */
    public setContext(type: string, details: PageContextDetails): void {
        this._entities.set(`${type}:${details.id}`, { type, id: details.id });
    }

    /** Snapshot of the entities currently on screen, for the report payload. */
    public getEntitiesOnScreen(): OnScreenEntity[] {
        return [...this._entities.values()];
    }

    /** Forget all on-screen entities (called on navigation). */
    public clear(): void {
        this._entities.clear();
    }
}
